import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { Table } from 'react-bootstrap';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { IMonthValue } from '../../util/DateUtils';

export interface ISorterSummaryReportRow {
  name: string;
  codes: string[];
  description: string;
  kg: number;
  eur: number;
  eurKg: number;
}

export interface ISorterSummaryReportProps {
  startDate: IMonthValue;
  endDate: IMonthValue;
  data: ISorterSummaryReportRow[];
  sorter: string;
}

const COLUMNS = [
  'name',
  'codes',
  'description',
  'kg',
  'eur',
  'eurKg'
];

const SORTABLE_COLUMNS = [
  'name',
  'description',
  'kg',
  'eur',
  'eurKg'
];

const SorterSummaryReport: React.FunctionComponent<ISorterSummaryReportProps> = (props: ISorterSummaryReportProps) => {
  const [dataTable] = useState(props.data);
  const [orderColumn, setOrderColumn] = useState('');
  const [orderDirection, setOrderDirection] = useState(1); // 1 | -1

  const setOrdering = (column: string) => {
    if (orderColumn !== column) {
      setOrderColumn(column);
      setOrderDirection(1);
    } else {
      setOrderDirection(-orderDirection);
    }
  }

  const getTableHeaders = () => {
    const headers: JSX.Element[] = [];
    COLUMNS.forEach((name) => {
      const classes = ['header'];
      let icon = null;
      let sortable = false;
      if (SORTABLE_COLUMNS.includes(name)) {
        classes.push('sortable');
        sortable = true;
      }
      if (name === orderColumn) {
        classes.push('sortby');
        icon = orderDirection === 1 ?
          <FontAwesomeIcon className="icon" icon="sort-up" color="white" /> : <FontAwesomeIcon className="icon" icon="sort-down" color="white" />
      } else if (sortable) {
        icon = <FontAwesomeIcon className="icon" icon="sort" color="lightgray" />
      }
      headers.push(
        <th key={name}>
          <span className={classes.join(' ')} onClick={() => sortable && setOrdering(name)}>
          <FormattedMessage id={`sortsummaryreports.browse.datatable.${name}`}/> {icon}
        </span></th>
      );
    })
    return headers;
  }

  const comparer = (a: ISorterSummaryReportRow, b: ISorterSummaryReportRow) => {
    let value = 0;
    if (orderColumn !== '') {
      switch(orderColumn) {
        case 'name':
          value = a.name.localeCompare(b.name);
          break;
        case 'description':
          value = a.description.localeCompare(b.description);
          break;
        case 'kg':
          value = a.kg - b.kg;
          break;
        case 'eur':
          value = a.eur - b.eur;
          break;
        case 'eurKg':
          value = a.eurKg - b.eurKg;
      }
    }
    return orderDirection * value;
  }

  const getDataTable = () => {
    const options: { }[] = [];
    if (dataTable && Array.isArray(dataTable)) {
      dataTable.sort(comparer).forEach((row : ISorterSummaryReportRow, index) => {
        options.push(
          <tr key={index}>
            <td>{row.name}</td>
            <td>{row.codes.join(', ')}</td>
            <td>{row.description}</td>
            <td className="right"><FormattedNumber value={row.kg} maximumFractionDigits={0} minimumFractionDigits={0}/></td>
            <td className="right"><FormattedNumber value={row.eur} maximumFractionDigits={2} minimumFractionDigits={2}/></td>
            <td className="right"><FormattedNumber value={row.eurKg} maximumFractionDigits={3} minimumFractionDigits={3}/></td>
          </tr>
        );
      });
    }
    return options;
  }

  return (
    <div className="sortReportImportResult generalReportImportResult">
      <div className="d-flex flex-md-row flex-column-reverse">
        <div className="reportResultHeader">
          <h3 className="monthRange">
            <FormattedDate
                value={new Date(props.startDate.year, props.startDate.month)}
                month="long"
                year="numeric"
              /> -<br/>
            <FormattedDate
                value={new Date(props.endDate.year, props.endDate.month)}
                month="long"
                year="numeric"
              />
          </h3>
          <h3 className="reportHeader">
            <FormattedMessage id="transportsummaryreports.import.handlingreport"/>
          </h3>
        </div>
        <div className="handlerNameHeader text-right ml-md-auto">
          <h1 className="float-right">{props.sorter.toUpperCase()}</h1>
        </div>
      </div>
      <div className="table-responsive" >
        <Table className="table table-striped">
          <thead className="reportDataHeader table table-bordered">
            <tr>
              { getTableHeaders() }
            </tr>
          </thead>
          <tbody className="reportData table table-bordered">
            { getDataTable() }
          </tbody>
        </Table>
      </div>
    </div>
  )
}

export default SorterSummaryReport;
